import React, { useState } from 'react';
import { Mail, ArrowRight, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { trackFormSubmission } from '../lib/analytics';

const NewsletterFormSection = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [state, setState] = useState<'idle' | 'check_inbox' | 'already'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      toast.error('Configuration error. Please try again later.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/send-newsletter-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({ email, name: name || undefined }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      if (data.already_subscribed) {
        setState('already');
        toast.success("You're already on the list.");
      } else {
        setState('check_inbox');
        toast.success('Check your inbox to confirm.');
        trackFormSubmission('Newsletter Signup', { has_name: !!name });
      }
      setEmail('');
      setName('');
    } catch (error: any) {
      console.error('Newsletter signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="newsletter-form" className="section-spacing-lg scroll-mt-20">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="relative p-8 sm:p-12 lg:p-16 rounded-3xl bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />

          <div className="relative z-10">
            {state === 'check_inbox' ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6">
                  <Check className="h-6 w-6 text-emerald-400" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4">Check your inbox</h2>
                <p className="text-gray-400 max-w-md mx-auto leading-relaxed">
                  We&rsquo;ve sent you a confirmation link. Click it to finish joining the list &mdash; you won&rsquo;t receive anything from us until you do.
                </p>
              </div>
            ) : state === 'already' ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 mb-6">
                  <Check className="h-6 w-6 text-white/70" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4">You&rsquo;re already on the list</h2>
                <p className="text-gray-400 max-w-md mx-auto leading-relaxed">
                  This email address is already subscribed. Thanks for being here &mdash; we&rsquo;ll be in touch when we&rsquo;ve got something worth sharing.
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-10">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-6">
                    <Mail className="h-6 w-6 text-primary/80" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">
                    Get the Inside Track
                  </h2>
                  <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
                    Monthly insights on AI, web tech, and what's actually working right now. No filler. First to know about new tools, resources, and offers.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
                  <div>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="First name (optional)"
                      className="w-full px-4 py-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-gray-600 focus:border-white/20 focus:ring-0 focus:outline-none transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      className="w-full px-4 py-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-gray-600 focus:border-white/20 focus:ring-0 focus:outline-none transition-colors text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full btn-primary flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Subscribing...' : (
                      <>
                        Join the List
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>

                <p className="text-center text-xs text-gray-600 mt-6">
                  One email a month max. Unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterFormSection;
